
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Profile } from "@/lib/types/auth";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { UserRoleSelect } from "@/components/admin/users/UserRoleSelect";
import { useUserManagementService } from "@/components/admin/users/UserManagementService";
import { toast } from "@/components/ui/use-toast";
import { Skeleton } from "@/components/ui/skeleton";

export default function UserRoleChecker() {
  const [email, setEmail] = useState("");
  const [searchPerformed, setSearchPerformed] = useState(false);
  const { handleRoleChange } = useUserManagementService();
  
  const { data: user, isLoading, refetch } = useQuery({
    queryKey: ['user-by-email', email],
    queryFn: async () => {
      if (!email) return null;
      
      // First, try to find the user profile by email in profiles table
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', email); // First try with ID in case the email is actually a UUID
      
      if (profilesError) {
        console.error("Error fetching profile by ID:", profilesError);
      }
      
      if (profilesData && profilesData.length > 0) {
        return profilesData[0] as Profile;
      }
      
      // Try to fetch the user from auth.users via a custom RPC function to get the ID
      const { data: authData, error: authError } = await supabase
        .rpc('get_user_id_by_email', { user_email: email });
      
      if (authError) {
        console.error("Error fetching user ID by email:", authError);
        throw new Error(`Could not find user with email: ${email}`);
      }
      
      if (!authData) {
        throw new Error(`No user found with email: ${email}`);
      }
      
      // Now get the profile using the ID
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', authData)
        .maybeSingle();
      
      if (profileError) {
        console.error("Error fetching profile:", profileError);
        throw new Error("Error fetching user profile");
      }
      
      return profile as Profile;
    },
    enabled: false, // Don't run the query automatically
  });
  
  const handleSearch = async () => {
    setSearchPerformed(true);
    await refetch();
  };
  
  const handleRoleChangeAndRefresh = async (userId: string, newRole: any) => {
    await handleRoleChange(userId, newRole);
    await refetch();
  };
  
  return (
    <div className="container mx-auto pt-6 px-6 space-y-6 max-w-4xl">
      <div className="flex flex-col space-y-4">
        <h1 className="text-2xl font-title font-bold tracking-tight text-primary">User Role Checker</h1>
        <p className="text-muted-foreground font-subtitle">Verify and update user roles by email</p>
        
        <div className="flex gap-2">
          <Input 
            value={email} 
            onChange={(e) => setEmail(e.target.value)} 
            placeholder="Enter user email"
            className="max-w-md"
          />
          <Button onClick={handleSearch}>Search</Button>
        </div>
        
        {searchPerformed && isLoading && (
          <Skeleton className="h-28 w-full max-w-md mt-4" />
        )}
        
        {searchPerformed && !isLoading && !user && (
          <Card className="max-w-md border-destructive/30 bg-destructive/5">
            <CardHeader>
              <CardTitle className="text-destructive">User Not Found</CardTitle>
              <CardDescription>No user found with the provided email.</CardDescription>
            </CardHeader>
          </Card>
        )}
        
        {searchPerformed && !isLoading && user && (
          <Card className="max-w-md">
            <CardHeader>
              <CardTitle>User Details</CardTitle>
              <CardDescription>Information for {email}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-2">
                <span className="font-semibold">Name:</span>
                <span>{user.first_name} {user.last_name}</span>
                
                <span className="font-semibold">ID:</span>
                <span className="truncate text-xs">{user.id}</span>
                
                <span className="font-semibold">School District:</span>
                <span>{user.school_district || '-'}</span>
                
                <span className="font-semibold">School:</span>
                <span>{user.school || '-'}</span>
                
                <span className="font-semibold">Position:</span>
                <span>{user.position || '-'}</span>
                
                <span className="font-semibold">Current Role:</span>
                <span>{user.role || 'No role'}</span>
                
                <span className="font-semibold">Status:</span>
                <span>{user.is_active !== false ? 'Active' : 'Inactive'}</span>
              </div>
            </CardContent>
            <CardFooter className="flex flex-col items-start gap-2">
              <div className="flex items-center gap-2">
                <span className="font-semibold">Change Role:</span>
                <UserRoleSelect 
                  userId={user.id} 
                  currentRole={user.role} 
                  onRoleChange={handleRoleChangeAndRefresh}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Set to 'tech' role to grant access to technical dashboard features.
              </p>
            </CardFooter>
          </Card>
        )}
      </div>
    </div>
  );
}
